// sessions management page
import React from "react";
import {
    useListSessionsQuery,
    useRevokeSessionMutation,
    useRevokeAllSessionsMutation,
} from "../../services/rtkApi/authServiceApi";
import { useAlert, useConfirm } from "../../ui/components/ConfirmDialog";
import "./sessions.css";

const SessionsPage: React.FC = () => {
    const alert = useAlert();
    const confirm = useConfirm();
    const { data: sessions, isLoading, refetch } = useListSessionsQuery();
    const [revokeSession, { isLoading: revoking }] = useRevokeSessionMutation();
    const [revokeAllSessions, { isLoading: revokingAll }] = useRevokeAllSessionsMutation();

    const handleRevokeSession = async (sessionId: string) => {
        const ok = await confirm({
            title: "End session?",
            message: "This will log out that device.",
            confirmLabel: "End",
            tone: "danger",
        });
        if (!ok) return;

        try {
            await revokeSession(sessionId).unwrap();
            refetch();
        } catch (err: any) {
            await alert({ title: "Revoke failed", message: err?.data?.error || "failed to revoke session" });
        }
    };

    const handleRevokeAllSessions = async () => {
        const ok = await confirm({
            title: "End all other sessions?",
            message: "You will stay logged in on this device.",
            confirmLabel: "End all",
            tone: "danger",
        });
        if (!ok) return;

        try {
            await revokeAllSessions().unwrap();
            refetch();
        } catch (err: any) {
            await alert({ title: "Revoke failed", message: err?.data?.error || "failed to revoke sessions" });
        }
    };

    const parseUserAgent = (ua: string | undefined) => {
        if (!ua) return { device: "Unknown Device", browser: "Unknown Browser" };

        // simple ua parsing
        const device = ua.includes("Mobile") ? "Mobile" :
            ua.includes("Tablet") ? "Tablet" : "Desktop";

        let browser = "Unknown";
        if (ua.includes("Firefox")) browser = "Firefox";
        else if (ua.includes("Chrome")) browser = "Chrome";
        else if (ua.includes("Safari")) browser = "Safari";
        else if (ua.includes("Edge")) browser = "Edge";

        return { device, browser };
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    if (isLoading) {
        return (
            <div className="sessions-page">
                <h1>Active Sessions</h1>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className="sessions-page">
            <div className="sessions-header">
                <div>
                    <h1>Active Sessions</h1>
                    <p>Manage devices where you're currently logged in</p>
                </div>
                {sessions && sessions.length > 1 && (
                    <button
                        onClick={handleRevokeAllSessions}
                        disabled={revokingAll}
                        className="revoke-all-btn"
                    >
                        {revokingAll ? "Logging out..." : "Log out other devices"}
                    </button>
                )}
            </div>

            <div className="sessions-list">
                {sessions?.map((session, index) => {
                    const { device, browser } = parseUserAgent(session.userAgent);
                    const isCurrentSession = index === 0; // first session is typically current

                    return (
                        <div
                            key={session.id}
                            className={`session-item ${isCurrentSession ? "current" : ""}`}
                        >
                            <div className="session-icon">
                                {device === "Mobile" ? "📱" : device === "Tablet" ? "📱" : "💻"}
                            </div>

                            <div className="session-info">
                                <div className="session-device">
                                    {device} • {browser}
                                    {isCurrentSession && <span className="current-badge">Current</span>}
                                </div>
                                <div className="session-details">
                                    {session.ipAddress && <span>{session.ipAddress}</span>}
                                    <span>Last active: {formatDate(session.lastActiveAt || session.createdAt)}</span>
                                </div>
                            </div>

                            {!isCurrentSession && (
                                <button
                                    onClick={() => handleRevokeSession(session.id)}
                                    disabled={revoking}
                                    className="revoke-btn"
                                >
                                    Log out
                                </button>
                            )}
                        </div>
                    );
                })}

                {(!sessions || sessions.length === 0) && (
                    <div className="no-sessions">
                        <p>No active sessions found</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SessionsPage;
